// ITAdvisory.jsx
import React, { useRef, useEffect, useState } from 'react';
import './ITAdvisory.css';
import it from "../../../assets/services/it.png";

import {
  IoAnalyticsOutline,
  IoWarningOutline,
  IoRefreshOutline,
  IoTimeOutline,
  IoWalletOutline,
  IoHappyOutline,
  IoBarChartOutline,
  IoCloudOutline,
  IoShieldCheckmarkOutline,
  IoGitBranchOutline,
  IoBulbOutline,
  IoRocketOutline,
  IoStatsChartOutline,
} from 'react-icons/io5';
import {
  FiActivity,
  FiSettings,
  FiServer,
  FiCloud,
  FiDollarSign,
  FiZap,
  FiCpu,
  FiTrendingUp,
  FiLayers,
} from 'react-icons/fi';
import {
  MdOutlineAnalytics,
  MdOutlineAutoAwesome,
  MdOutlineCloudQueue,
} from 'react-icons/md';

/* ── Data ─────────────────────────────────────────────────── */
const deliverables = [
  { title: "IT Operations Assessment & Maturity Evaluation", icon: <FiActivity /> },
  { title: "IT Service Management (ITSM) Optimization", icon: <FiSettings /> },
  { title: "Infrastructure Modernization Strategy", icon: <FiServer /> },
  { title: "Cloud Operations Advisory", icon: <FiCloud /> },
  { title: "IT Process Automation Strategy", icon: <MdOutlineAutoAwesome /> },
  { title: "Incident, Problem & Change Management Optimization", icon: <IoWarningOutline /> },
  { title: "IT Cost Optimization & Resource Efficiency", icon: <FiDollarSign /> },
  { title: "Monitoring, Observability & Performance Strategy", icon: <IoAnalyticsOutline /> },
  { title: "Business Continuity & Disaster Recovery Planning", icon: <IoRefreshOutline /> },
  { title: "AI Ops & Intelligent Operations Advisory", icon: <FiCpu /> },
];

const focusAreas = [
  { text: "IT Service Management (ITSM)", icon: <FiSettings /> },
  { text: "Infrastructure & Cloud Operations", icon: <MdOutlineCloudQueue /> },
  { text: "Automation & AI Ops", icon: <FiCpu /> },
  { text: "Governance & Compliance", icon: <IoShieldCheckmarkOutline /> },
  { text: "Operational Resilience", icon: <IoRefreshOutline /> },
];

const outcomes = [
  { text: "Improved IT operational efficiency", icon: <IoStatsChartOutline /> },
  { text: "Reduced downtime & faster issue resolution", icon: <IoTimeOutline /> },
  { text: "Lower infrastructure & operational costs", icon: <IoWalletOutline /> },
  { text: "Increased service reliability & performance", icon: <FiZap /> },
  { text: "Enhanced operational visibility & governance", icon: <IoAnalyticsOutline /> },
  { text: "Faster response to business demands", icon: <IoRocketOutline /> },
  { text: "Improved scalability & cloud readiness", icon: <IoCloudOutline /> },
  { text: "Better employee & customer experience", icon: <IoHappyOutline /> },
];

const whyBairees = [
  { text: "Strategic advisory with practical operational expertise", icon: <FiTrendingUp /> },
  { text: "Modernize IT operations without disrupting business continuity", icon: <IoRefreshOutline /> },
  { text: "Build agile, resilient, and intelligent IT operating environments", icon: <IoBulbOutline /> },
  { text: "Focus on long-term digital growth and scalability", icon: <IoBarChartOutline /> },
  { text: "End-to-end IT transformation expertise", icon: <IoGitBranchOutline /> },
  { text: "AI-driven operational intelligence frameworks", icon: <MdOutlineAnalytics /> },
];

/* ── Animated counter hook ───────────────────────────────── */
function useCountUp(target, duration = 1800, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return value;
}

/* ── Stat card with counter ──────────────────────────────── */
function StatCard({ value, suffix, label, delay, inView }) {
  const count = useCountUp(value, 1600, inView);
  return (
    <div className="ita-stat" style={{ animationDelay: `${delay}ms` }}>
      <span className="ita-stat__number">
        {count}{suffix}
      </span>
      <span className="ita-stat__label">{label}</span>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────── */
const ITAdvisory = () => {
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="ita">

      {/* ── HERO ──────────────────────────────────────────── */}
      <section className="ita-hero">
        <div className="ita-hero__bg-grid" aria-hidden />
        <div className="ita-hero__container">
          <div className="ita-hero__content">
            <p className="ita-hero__eyebrow">IT Advisory Services</p>
            <h1 className="ita-hero__title">
              IT Operations<br />
              <span className="ita-hero__title--gold">Advisory</span>
            </h1>
            <p className="ita-hero__tagline">Modernize. Automate. Scale with Confidence.</p>
            <p className="ita-hero__desc">
              Modern IT operations face constant pressure to deliver stability, speed, security,
              and cost efficiency — all while supporting rapid business transformation. Bairees
              helps enterprises move from reactive IT management to proactive, resilient,
              and business-aligned operations.
            </p>
            <div className="ita-hero__actions">
              <a href="/contact"><button className="ita-btn ita-btn--primary">Talk to Bairees Today <span className="arrow">→</span></button></a>
              <a href="/contact"> <button className="ita-btn ita-btn--ghost">View Framework</button></a>
            </div>
          </div>

          <div className="ita-hero__aside">
            <div className="ita-hero__card">
              <div className="ita-hero__card-icon"><FiLayers /></div>
              <p className="ita-hero__card-label">End-to-End</p>
              <p className="ita-hero__card-value">IT Transformation</p>
            </div>
            <div className="ita-hero__card">
              <div className="ita-hero__card-icon"><IoBulbOutline /></div>
              <p className="ita-hero__card-label">Intelligent</p>
              <p className="ita-hero__card-value">Operations Advisory</p>
            </div>
            <div className="ita-hero__card">
              <div className="ita-hero__card-icon"><IoShieldCheckmarkOutline /></div>
              <p className="ita-hero__card-label">Resilient</p>
              <p className="ita-hero__card-value">Governance Frameworks</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ─────────────────────────────────────── */}
      <section className="ita-stats" ref={statsRef}>
        <div className="ita-stats__inner">
          <StatCard value={200} suffix="+" label="IT assessments delivered" delay={0} inView={statsVisible} />
          <div className="ita-stats__divider" />
          <StatCard value={40} suffix="%" label="Avg. cost reduction" delay={150} inView={statsVisible} />
          <div className="ita-stats__divider" />
          <StatCard value={98} suffix="%" label="Client satisfaction rate" delay={300} inView={statsVisible} />
          <div className="ita-stats__divider" />
          <StatCard value={15} suffix="+" label="Years of operational depth" delay={450} inView={statsVisible} />
        </div>
      </section>

      {/* ── WHAT WE DELIVER ───────────────────────────────── */}
      <section className="ita-section">
        <div className="ita-section__container">
          <div className="ita-section__header">
            <p className="ita-eyebrow">Capabilities</p>
            <h2 className="ita-section__title">What We Deliver</h2>
            <p className="ita-section__subtitle">
              A comprehensive suite of advisory services to transform your IT operations.
            </p>
          </div>
          <div className="ita-deliverables">
            {deliverables.map((item, i) => (
              <div key={i} className="ita-deliverable" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="ita-deliverable__icon">{item.icon}</div>
                <p className="ita-deliverable__text">{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FRAMEWORK ─────────────────────────────────────── */}
      {/* <section className="ita-framework">
        <div className="ita-framework__container">
          <div className="ita-framework__header">
            <p className="ita-eyebrow ita-eyebrow--light">Our Approach</p>
            <h2 className="ita-framework__title">IT Operations Advisory Framework</h2>
          </div>
          <div className="ita-framework__visual">
            <img src={it} alt="IT Operations Advisory Framework" className="ita-framework__img" />
          </div>
        </div>
      </section> */}

      {/* ── FOCUS + OUTCOMES ──────────────────────────────── */}
      <section className="ita-section ita-section--alt">
        <div className="ita-section__container">
          <div className="ita-two-col">

            {/* Focus Areas */}
            <div className="ita-panel">
              <p className="ita-eyebrow">Where We Focus</p>
              <h2 className="ita-panel__title">Key Focus Areas</h2>
              <ul className="ita-list">
                {focusAreas.map((area, i) => (
                  <li key={i} className="ita-list__item">
                    <span className="ita-list__icon">{area.icon}</span>
                    <span className="ita-list__text">{area.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Outcomes */}
            <div className="ita-panel">
              <p className="ita-eyebrow">What You Gain</p>
              <h2 className="ita-panel__title">Business Outcomes</h2>
              <ul className="ita-list">
                {outcomes.map((outcome, i) => (
                  <li key={i} className="ita-list__item">
                    <span className="ita-list__icon">{outcome.icon}</span>
                    <span className="ita-list__text">{outcome.text}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* ── WHY BAIREES ───────────────────────────────────── */}
      <section className="ita-why">
        <div className="ita-why__container">
          <div className="ita-why__header">
            <p className="ita-eyebrow ita-eyebrow--light">Our Edge</p>
            <h2 className="ita-why__title">Why Bairees</h2>
            <p className="ita-why__subtitle">
              We combine strategic advisory with practical expertise to modernize IT
              operations without disrupting business continuity.
            </p>
          </div>
          <div className="ita-why__grid">
            {whyBairees.map((reason, i) => (
              <div key={i} className="ita-why__card">
                <div className="ita-why__card-icon">{reason.icon}</div>
                <p className="ita-why__card-text">{reason.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────── */}
      <section className="next-section">
        <div className="next-container">
          <div className="next-left">
            <h2 className="next-title">Transform IT Operations Into a Strategic Business Advantage</h2>
            <p className="next-subtitle">
              Scalable frameworks, automation, cloud optimization, and AI-driven operational intelligence.
            </p>
            <div className="ita-cta__chips">
              <span>✓ Enable smarter operations</span>
              <span>✓ Reduce complexity</span>
              <span>✓ Improve reliability</span>
              <span>✓ Scale with confidence</span>
            </div>
          </div>
          <div className="next-right">
            <a href="/contact"> <button className="next-btn">Talk to Bairees Today <span className="arrow">→</span></button></a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ITAdvisory;